export { tencentCloudConfigGetToken } from './token';
import type CosType from 'cos-js-sdk-v5';
import type { Err, GetUploadResult } from './token';
import { getToken, UpdateClientType } from './token';
import axios from 'axios';
import dayjs from 'dayjs';
import { upload } from 'qiniu-js';

const cancelTokenSource = axios.CancelToken.source();
export interface UploadFileParams {
  file: File;
  /** 自定义获取上传地址请求 */
  customUploadCallApi?: (params: { suffix: string }) => Promise<GetUploadResult>;
  onProgress?: (uploadProgress: {
    data?: CosType.ProgressInfo;
    total: {
      percent: number;
    };
  }) => void;
  onSuccess?: (data: { url: string }) => void;
  onError?: (error: any) => void;
  cancel?: (cancel: () => void) => void;
}

let COS: typeof CosType;

if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  COS = require('cos-js-sdk-v5');
}
/** 是否调用自定义上传方法 */
async function getUploadToken({
  suffix,
  customUploadCallApi,
}: {
  suffix: string;
  customUploadCallApi?: (params: { suffix: string }) => Promise<GetUploadResult>;
}): Promise<GetUploadResult> {
  if (customUploadCallApi) {
    return customUploadCallApi({
      suffix,
    });
  }
  return getToken({
    suffix,
  });
}

type RespUploadFile = (GetUploadResult & { url: string }) & Partial<Err>;

export async function uploadFile({
  file,
  customUploadCallApi,
  onProgress,
  onSuccess,
  onError,
  cancel,
}: UploadFileParams): Promise<RespUploadFile> {
  const suffix = getSuffix(file);

  // 获取信息
  const uploadResult = await getUploadToken({ suffix, customUploadCallApi });
  const { token } = uploadResult as GetUploadResult;

  if (uploadResult.osClientType === UpdateClientType.S3_CDN || uploadResult.osClientType === UpdateClientType.S3) {
    cancel?.(() => {
      cancelTokenSource.cancel('cancel');
    });
    const resp = s3UploadFile();
    return resp;
  }
  if (uploadResult.osClientType === UpdateClientType.QINIU) {
    const resp = qiniuUploadFile();

    return resp;
  }

  // cos 初始化
  const cos = initCos();

  // COS 文件上传
  const result = cosUploadFile();

  cancel?.(() => {
    cos.cancelTask(cos.getTaskList()[0].id);
  });

  return result;
  /**
   * @description: 七牛文件上传
   */
  function qiniuUploadFile(): Promise<RespUploadFile> {
    if (!token?.key || !token?.token) {
      console.error('token is not string');
      return Promise.reject({ error: { Code: 500, Message: 'token is not string' } });
    }
    const observable = upload(file, token.key, token.token);
    return new Promise((resolve, reject) => {
      observable.subscribe({
        next: (uploadProgress) => {
          onProgress?.({
            total: {
              percent: uploadProgress.total.percent,
            },
          });
        },
        error: (err) => {
          onError?.(err);
          reject!(err);
        },
        complete: (result) => {
          const data = { ...result, key: token?.key, url: token?.url, urlprefix: token?.urlprefix };
          onSuccess?.(data);
          resolve!(data);
        },
      });
    });
  }
  /**
   * @description: COS 初始化
   */
  function initCos() {
    return new COS({
      // getAuthorization 必选参数
      getAuthorization: async (_, callback) => {
        if (typeof token?.token !== 'string') {
          console.error('token is not string');
          return;
        }
        callback({
          TmpSecretId: uploadResult.token?.tmpSecretId ?? '',
          TmpSecretKey: uploadResult.token?.tmpSecretKey ?? '',
          SecurityToken: token?.token ?? '',
          // 建议返回服务器时间作为签名的开始时间，避免用户浏览器本地时间偏差过大导致签名错误
          StartTime: dayjs().unix(),
          ExpiredTime: dayjs(uploadResult.token?.expiresAt).unix(),
        });
      },
    });
  }

  /**
   * @description: COS 文件上传
   */
  function cosUploadFile(): Promise<RespUploadFile> {
    return new Promise((resolve, reject) => {
      cos
        .uploadFile({
          Bucket: uploadResult.token?.bucketName ?? '',
          Region: uploadResult.token?.regionName ?? '',
          Key: uploadResult.token?.keys?.[0] ?? '',
          Body: file,
          SliceSize:
            1024 * 1024 * 5 /* 触发分块上传的阈值，超过5MB使用分块上传，小于5MB使用简单上传。可自行设置，非必须 */,
          onProgress: function (progressData) {
            const data = {
              data: progressData,
              total: {
                percent: progressData.percent * 100,
              },
            };
            onProgress?.(data);
          },
        })
        .then(() => {
          const data = {
            ...uploadResult,
            url: `${uploadResult.token?.urlprefix ?? ''}${uploadResult.token?.keys?.[0] ?? ''}`,
          };
          onSuccess?.(data);
          resolve!(data);
        })
        .catch((err) => {
          onError?.(err);
          reject!(err);
        });
    });
  }

  /** S3文件上传 */
  function s3UploadFile(): Promise<RespUploadFile> {
    return new Promise((resolve, reject) => {
      const formData = new FormData();

      formData.append('file', file);
      // 判断token是否是对象
      if (typeof token === 'string') {
        reject!({ error: { Code: 500, Message: 'token is not object' } });
        return;
      }
      axios
        .put(token?.url ?? '', file, {
          cancelToken: cancelTokenSource.token,
          headers: {
            'content-type': 'application/octet-stream',
            ...(token?.requestHeaders ?? {}),
          },
        })
        .then(() => {
          const url = token?.url?.split('?')[0];
          const data = { ...uploadResult, url: url ?? '' };
          onSuccess?.(data);
          resolve!(data);
        })
        .catch((err) => {
          onError?.(err);
          reject!(err);
        });
    });
  }
}

export function getSuffix(file: File) {
  return file.name.substring(file.name.lastIndexOf('.')).substr(1);
}
